import React from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import type { CopilotAnswer } from './copilotTypes';
import { Histogram } from './charts/Histogram';
import { CalendarHeatmap } from './charts/CalendarHeatmap';
import { KpiCards } from './charts/KpiCards';
import { TrendLine } from './charts/TrendLine';
import { ScatterRegression } from './charts/ScatterRegression';
import { StackedArea } from './charts/StackedArea';
import { PolarBar } from './charts/PolarBar';
import { Bubble } from './charts/Bubble';
import { StatCard } from './charts/StatCard';
import { FallbackTable } from './FallbackTable';
import { EscapeHatch } from './EscapeHatch';
import { AnalysisView } from './AnalysisView';
import { Markdown } from './Markdown';
import { tokens } from './tokens';

/**
 * The single dispatcher from a validated CopilotAnswer to its renderer. Every answer kind the
 * Copilot can return flows through here; an unknown kind or chart degrades to nothing rendered
 * (the bubble's prose still stands) rather than throwing.
 *
 * `onClarify` re-asks one of a clarify turn's option chips (with history) when clicked.
 */
// Memoized on the ANSWER identity only: the parent re-renders on every streamed token (message.text),
// but the validated answer is set once and never mutates, so we skip those re-renders (which otherwise
// cascade into every chart). onClarify (a fresh closure each render) is intentionally ignored — it reads
// refs, so a "stale" one still re-asks correctly; comparing it would defeat the memo.
function AnswerViewImpl({
  answer,
  onClarify,
}: {
  answer: CopilotAnswer;
  onClarify?: (q: string) => void;
}) {
  const answerScope = 'scope' in answer ? answer.scope : undefined;
  const scopeCaption = answerScope
    ? answerScope.population === 'cohort'
      ? `All participants${answerScope.source ? ` · ${answerScope.source === 'fitbit' ? 'Fitbit' : 'HealthKit'}` : ''}`
      : `${answerScope.participantId}${answerScope.source ? ` · ${answerScope.source === 'fitbit' ? 'Fitbit' : 'HealthKit'}` : ''}`
    : null;
  const withScope = (content: React.ReactNode) => (
    <div>
      {scopeCaption && (
        <Typography sx={{ fontSize: 12, color: tokens.text.muted, mb: 0.75, fontFamily: tokens.fontFamily }}>
          Data scope: {scopeCaption}
        </Typography>
      )}
      {content}
    </div>
  );
  // In the transcript the assistant card sits under the user's question turn, which already shows the
  // question — so the card title (which the route sets to the verbatim question) is a redundant echo.
  // Pass an empty title; ChartCard omits the title row when it's blank.
  if (answer.kind === 'fallback') return withScope(<FallbackTable title="" table={answer.table} />);
  if (answer.kind === 'escape') return withScope(<EscapeHatch title="" option={answer.option as any} />);

  // Prose: a non-factual qualitative reply — render the narrative as markdown, no chart.
  if (answer.kind === 'prose') return withScope(<Markdown text={answer.narrative} />);
  if (answer.kind === 'email_sent') {
    return withScope(
      <div>
        <Markdown text={answer.narrative} />
        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: tokens.text.secondary, fontFamily: tokens.fontFamily }}>
            <strong>Recipients:</strong> {answer.recipientIds.join(', ') || 'None'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.text.secondary, fontFamily: tokens.fontFamily }}>
            <strong>Subject:</strong> {answer.subject}
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.text.secondary, fontFamily: tokens.fontFamily, whiteSpace: 'pre-wrap' }}>
            <strong>Body:</strong>{'\n'}{answer.body}
          </Typography>
        </Stack>
      </div>,
    );
  }
  if (answer.kind === 'analysis') return withScope(<AnalysisView answer={answer} />);

  // Clarify: the narrative as markdown plus option chips that re-ask on click (no data query ran).
  if (answer.kind === 'clarify') {
    const clarificationQuestion = (option: string) => {
      // A chip is an answer to a pending request, not a brand-new question. Repeating the original
      // wording keeps participant, metric, and analytical intent intact even if client context is
      // cleared or a researcher opens the transcript later.
      if (answer.clarificationType === 'timeframe') return `${answer.title} for ${option.toLowerCase()}`;
      if (answer.clarificationType === 'source') return `${answer.title} using ${option}`;
      if (answer.clarificationType === 'participant') return `${answer.title} for participant ${option}`;
      return option;
    };
    return withScope(
      <div>
        <Markdown text={answer.narrative} />
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
          {answer.options.map((opt) => (
            <Chip
              key={opt}
              label={opt}
              onClick={onClarify ? () => onClarify(clarificationQuestion(opt)) : undefined}
              variant="outlined"
              size="small"
              clickable={!!onClarify}
              sx={{
                fontFamily: tokens.fontFamily,
                borderColor: tokens.surface.border,
                color: tokens.text.secondary,
                '&:focus-visible': { outline: `2px solid ${tokens.brand.cardinal}`, outlineOffset: '2px' },
              }}
            />
          ))}
        </Stack>
      </div>,
    );
  }

  if (answer.kind !== 'chart') return null;

  const { spec } = answer;
  const title = ''; // echoed-question title omitted in the transcript (the user turn shows it)
  const d = answer.data as any;
  switch (spec.chart) {
    case 'histogram': return withScope(<Histogram title={title} spec={spec} data={d} />);
    case 'calendar_heatmap': return withScope(<CalendarHeatmap title={title} spec={spec} data={d} />);
    case 'kpi': return withScope(<KpiCards title={title} spec={spec} data={d} />);
    case 'trend_line': return withScope(<TrendLine title={title} spec={spec} data={d} />);
    case 'scatter_regression': return withScope(<ScatterRegression title={title} spec={spec} data={d} />);
    case 'stacked_area': return withScope(<StackedArea title={title} spec={spec} data={d} />);
    case 'polar_bar': return withScope(<PolarBar title={title} spec={spec} data={d} />);
    case 'bubble': return withScope(<Bubble title={title} spec={spec} data={d} />);
    // Stat: a single backend-computed figure; the summary is the backend-owned takeaway (never model-authored).
    case 'stat': return withScope(<StatCard title={title} data={d} summary={answer.summary} />);
    default: return null;
  }
}

export const AnswerView = React.memo(AnswerViewImpl, (prev, next) => prev.answer === next.answer);
